import { AuthContext } from "@@components";
import { MainLayout } from "@@containers";
import React, { useContext, useMemo, useRef } from "react";
import { createBrowserRouter, LoaderFunctionArgs, redirect, RouteObject, RouterProvider, useParams, useSearchParams } from "react-router-dom";
import Spinner from "../../common/components/Spinner";
import ExpressionEvaluatorContext from "../../ExpressionsModule/Contexts/ExpressionEvaluatorContext";
import { HomeView } from "../../views";
import NotFoundView from "../../views/NotFound/NotFoundView";
import ViewComponent from "../Components/View/ViewComponent";
import BundleContext from "../Contexts/BundleContext";
import { DesignerPreviewRoute } from "../Routes/DesignerPreviewRoute";
import { NavigationService } from "../Services/NavigationService";
import { DesignerModeLayout } from "./DesignerModeLayout";

/**
 * Thin wrapper that resolves route params and search params before passing them
 * as `input` to ViewComponent. Because this component lives inside the router
 * context, `useParams()` always returns the fully-matched params — no empty
 * renders that would cause children to evaluate $input.Id as undefined.
 */
const ViewRouteWrapper: React.FC<{ viewId: string }> = ({ viewId }) => {
  const bundle = useContext(BundleContext);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const input = useMemo(() => ({ ...Object.fromEntries(searchParams.entries()), ...params }), [params, searchParams]);
  const component = bundle.ViewById[viewId];

  if (!component) {
    return <NotFoundView />;
  }

  return <ViewComponent component={component} input={input} />;
};

const DynamicRouteContainer: React.FC = () => {
  const auth = useContext(AuthContext);
  const bundle = useContext(BundleContext);
  const expressionContext = useContext(ExpressionEvaluatorContext);
  const hasInitialized = useRef(false);

  const router = useMemo(() => {
    // Mark as initialized on first router creation
    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
    function protectedLoader({ request }: LoaderFunctionArgs) {
      if (!auth.isAuthenticated) {
        const params = new URLSearchParams();
        params.set("from", new URL(request.url).pathname);
        return redirect(`/?${params.toString()}`);
      }
      return null;
    }

    const children: RouteObject[] = bundle.Views.filter((view) => view.RoutePath !== undefined).map((view) => ({
      id: view.Id,
      path: view.RoutePath,
      loader: view.RequiresAuthentication ? protectedLoader : undefined,
      element: <ViewRouteWrapper key={view.Id} viewId={view.Id} />,
    }));

    if (children.length === 0) {
      children.push({
        path: "/",
        loader: undefined,
        element: <HomeView />,
      });
    }

    const createdRouter = createBrowserRouter([
      {
        id: "designer-mode-wrapper",
        path: "/",
        Component: DesignerModeLayout,
        children: [
          {
            id: "root",
            path: "/",
            Component: MainLayout,
            children: children,
            errorElement: <NotFoundView />,
          },
          {
            path: "/designer-preview",
            element: <DesignerPreviewRoute />,
          },
          {
            path: "*",
            Component: MainLayout,
            children: [
              {
                path: "*",
                element: <NotFoundView />,
              },
            ],
          },
        ],
      },
    ]);

    const navService = expressionContext?.GetProperty<NavigationService>(NavigationService.KEY);
    navService?.setNavigate((path) => createdRouter.navigate(path));

    return createdRouter;
  }, [auth.isAuthenticated, bundle.Views, expressionContext]);

  // Don't render the router until it has been initialized with routes
  if (!hasInitialized.current) {
    return <Spinner />;
  }

  return <RouterProvider router={router} />;
};

export default DynamicRouteContainer;
